import React, { useRef, useState } from 'react';
import { IonContent, IonHeader, IonToolbar, IonButtons, IonButton, IonTitle, IonModal, IonIcon } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { GameSetup as GameSetupType } from './types';
import WelcomeScreen from './WelcomeScreen';
import GameSetupForm from './GameSetupForm';
import AdvancedSettings from './AdvancedSettings';
import AddPlayers from './AddPlayers';
import PlayerDetails from './PlayerDetails';
import GameOverview from './GameOverview';
import GolfIcon from '../GolfIcon';

interface GameSetupProps {
  isOpen: boolean;
  onClose: () => void;
}

const GameSetup: React.FC<GameSetupProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const modal = useRef<HTMLIonModalElement>(null);
  const [step, setStep] = useState<number>(0);
  const [gameData, setGameData] = useState<Partial<GameSetupType>>({
    name: '',
    date: new Date().toISOString(),
    holes: 18,
    startingHole: 1,
    format: 'strokeplay',
    players: [],
    contests: {
      longestDrive: false,
      closestToPin: false,
    },
    teeOptions: {
      default: 'white',
    },
    useHandicaps: true,
  });

  const handleNext = (data: Partial<GameSetupType>) => {
    setGameData({ ...gameData, ...data });
    setStep(step + 1);
  };

  const resetState = () => {
    setStep(0);
    setGameData({
      name: '',
      date: new Date().toISOString(),
      holes: 18,
      startingHole: 1,
      format: 'strokeplay',
      players: [],
      contests: {
        longestDrive: false,
        closestToPin: false,
      },
      teeOptions: {
        default: 'white',
      },
      useHandicaps: true,
    });
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    } else {
      onClose();
    }
  };

  const handleStartGame = () => {
    // Here you would typically save the game data to your backend
    console.log('Starting game with data:', gameData);

    // Close the modal and potentially navigate to the scorecard
    onClose();
    // Reset the state for next time
    resetState();
    // You could emit an event here that the parent component could listen for
    // to navigate to the scorecard after the modal is closed
  };

  const getStepTitle = () => {
    switch (step) {
      case 0: return t('New Golf Game');
      case 1: return t('Game Setup');
      case 2: return t('Add Players');
      case 3: return t('Advanced Settings');
      case 4: return t('Player Details');
      case 5: return t('Game Overview');
      default: return t('Golf Game');
    }
  };

  // Animation variants for page transitions
  const pageVariants = {
    initial: {
      opacity: 0,
      x: '100%',
    },
    in: {
      opacity: 1,
      x: 0,
    },
    out: {
      opacity: 0,
      x: '-100%',
    },
  };

  const pageTransition = {
    type: 'tween',
    ease: 'anticipate',
    duration: 0.3,
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <WelcomeScreen
            onCreateGame={() => setStep(1)}
            onJoinGame={() => console.log('Join game functionality not implemented yet')}
          />
        );
      case 1:
        return (
          <GameSetupForm
            initialData={gameData}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <AddPlayers
            initialData={gameData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <AdvancedSettings
            initialData={gameData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <PlayerDetails
            initialData={gameData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 5:
        return (
          <GameOverview
            gameData={gameData}
            onStartGame={handleStartGame}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  return (
    <IonModal
      ref={modal}
      isOpen={isOpen}
      onDidDismiss={() => {
        onClose();
        resetState();
      }}
      className="game-setup-modal"
      initialBreakpoint={1}
      breakpoints={[0, 1]}
    >
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={() => {
              onClose();
              resetState();
            }}>
              <GolfIcon name="close" className="size-5" />
            </IonButton>
          </IonButtons>
          <IonTitle>{getStepTitle()}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial="initial"
            animate="in"
            exit="out"
            variants={pageVariants}
            transition={pageTransition}
            className="h-full"
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </IonContent>
    </IonModal>
  );
};

export default GameSetup;
